import { gql } from '@apollo/client';
import { track } from '@mnd-apps/publish/lib/tracker';
import { Button, Dialog, Input, MynewsdeskLogo, Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { PURPLE_700, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  useChangeEmailVerificationMutation,
  useResendEmailVerificationMutation,
} from '../../generated/hooks';
import mndUrl, { SubDomain } from '../../lib/mndUrl';

gql`
  mutation resendEmailVerification {
    userActivationEmailResend(input: {}) {
      error {
        message
      }
      success
    }
  }

  mutation changeEmailVerification($email: String!) {
    userActivationEmailUpdate(input: { email: $email }) {
      error {
        message
      }
      user {
        id
        email
      }
    }
  }
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${SPACING_2} ${SPACING_2} 0 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_2};

  h1 {
    margin-top: 0;
  }

  button {
    height: auto;
    text-decoration: underline;
    color: ${PURPLE_700};
  }
`;

const EmailVerification = ({ email }: { email: string }) => {
  const { t } = useTranslation();
  const { showFailureToast, showSuccessToast } = useToasts();
  const [changeEmailModal, setChangeEmailModal] = useState(false);
  const [updateEmail, setUpdateEmail] = useState(email);

  useEffect(() => {
    track({ event: 'EmailVerification: View Page' });
  }, []);

  const [resendEmail] = useResendEmailVerificationMutation({
    onError: () => showFailureToast(),
    onCompleted: data => {
      if (data.userActivationEmailResend?.error) {
        return showFailureToast(data.userActivationEmailResend.error.message);
      }
      showSuccessToast(t('email_verification_link_resent'));
    },
  });

  const [changeEmail, { loading: changeEmailLoading }] = useChangeEmailVerificationMutation({
    onError: () => showFailureToast(),
    onCompleted: data => {
      if (data.userActivationEmailUpdate?.error)
        return showFailureToast(data.userActivationEmailUpdate?.error.message);
      setChangeEmailModal(false);
      showSuccessToast(t('email_verification_update_link_resent'));
    },
  });

  return (
    <>
      <TopWrapper>
        <a href={mndUrl(SubDomain.Publish, '/user/signout')}>{t('menu_user_logout')}</a>
      </TopWrapper>
      <Wrapper>
        <Content>
          <MynewsdeskLogo width="162" height="28" />
          <h1>{t('email_verification_takeover_title')}</h1>
          <Text as="p" variant="body">
            <Trans
              i18nKey="email_verification_takeover_info"
              values={{
                email,
              }}
            />
          </Text>
          <Text as="p" variant="body">
            <Trans
              i18nKey="email_verification_takeover_cta_text"
              components={{
                changeCta: (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error - child received from translation
                  <Button
                    type="button"
                    configuration="text"
                    onClick={() => setChangeEmailModal(true)}
                  />
                ),
                resendCta: (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error - child received from translation
                  <Button type="button" configuration="text" onClick={() => resendEmail()} />
                ),
              }}
            />
          </Text>
        </Content>
      </Wrapper>
      <Dialog
        open={changeEmailModal}
        onClose={() => setChangeEmailModal(false)}
        description={t('email_verification_update_modal_title')}
        confirmAction={{
          text: t('common_update'),
          onClick: () =>
            changeEmail({
              variables: {
                email: updateEmail,
              },
            }),
          loading: changeEmailLoading,
          disabled: !updateEmail || updateEmail === email,
        }}
      >
        <Input
          label={'email'}
          id="name"
          value={updateEmail}
          onChange={e => setUpdateEmail(e.target.value)}
        />
      </Dialog>
    </>
  );
};

export default EmailVerification;
